import { useState } from "react";
import { useModuleBEM, IconNameEnum } from "@b2bportal/core-themes";
import { HotelNavigationUtility } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TimePicker, InputText, Icon, Checkbox } from "@tenant/ui";
import dayjs from "dayjs";
import { ActionButton, DateRangePicker } from "../../../../../components";
import styles from "./CarsSearchBar.module.scss";
import { useSessionContext } from "@/components/AuthModule/SessionContext";
import { CarLocationAutocomplete } from "@/components/CarLocationAutocomplete";
import { buildCarsAvailabilityUrl } from "@/utils/commerce/urls";
import type { CarsLocationSuggestion } from "@/utils/fetchCarsLocations";
import { saveRecentCarSearch } from "@/utils/useRecentSearches";

const DEFAULT_PICKUP_TIME_HOUR = 10;
const DEFAULT_PICKUP_DAYS_AHEAD = 1;
const DEFAULT_DROPOFF_DAYS_AHEAD = 2;

export const SearchBarContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [, cn] = useModuleBEM(styles, "CarsSearchBar");

  return <div className={cn("content")}>{children}</div>;
};

export const CarsSearchBar = () => {
  const [, cn] = useModuleBEM(styles, "CarsSearchBar");
  const { t } = useI18nContext();
  const { sessionInfo } = useSessionContext();

  const [pickupDate, setPickupDate] = useState<string | undefined>(
    dayjs()
      .add(DEFAULT_PICKUP_DAYS_AHEAD, "day")
      .hour(DEFAULT_PICKUP_TIME_HOUR)
      .minute(0)
      .second(0)
      .toISOString()
  );
  const [dropoffDate, setDropoffDate] = useState<string | undefined>(
    dayjs()
      .add(DEFAULT_DROPOFF_DAYS_AHEAD, "days")
      .hour(DEFAULT_PICKUP_TIME_HOUR)
      .minute(0)
      .second(0)
      .toISOString()
  );

  const [pickupTime, setPickupTime] = useState<string | undefined>(
    dayjs().hour(DEFAULT_PICKUP_TIME_HOUR).minute(0).second(0).toISOString()
  );
  const [dropoffTime, setDropoffTime] = useState<string | undefined>(
    dayjs().hour(DEFAULT_PICKUP_TIME_HOUR).minute(0).second(0).toISOString()
  );
  const [driverAge, setDriverAge] = useState<string>("26");

  const [pickupLocation, setPickupLocation] = useState<
    CarsLocationSuggestion | undefined
  >(undefined);
  const [dropoffLocation, setDropoffLocation] = useState<
    CarsLocationSuggestion | undefined
  >(undefined);
  const [returnDifferent, setReturnDifferent] = useState<boolean>(false);

  const handleSearch = () => {
    if (
      !pickupDate ||
      !dropoffDate ||
      !pickupTime ||
      !dropoffTime ||
      !driverAge ||
      !pickupLocation ||
      (returnDifferent && !dropoffLocation)
    ) {
      return;
    }

    saveRecentCarSearch({
      pickupDate: HotelNavigationUtility.formatDateForUrl(pickupDate),
      dropoffDate: HotelNavigationUtility.formatDateForUrl(dropoffDate),
      pickupTime,
      dropoffTime,
      pickupLocation: {
        label: pickupLocation.label,
        identifier: pickupLocation.identifier,
      },
      dropoffLocation: {
        label:
          returnDifferent && dropoffLocation
            ? dropoffLocation.label
            : pickupLocation.label,
        identifier:
          returnDifferent && dropoffLocation
            ? dropoffLocation.identifier
            : pickupLocation.identifier,
      },
      driverAge,
      customerId: sessionInfo?.hopperUserId,
    });

    const url = buildCarsAvailabilityUrl({
      pickupDate,
      dropoffDate,
      pickupTime,
      dropoffTime,
      driverAge,
      pickupLocationIdentifier: pickupLocation.identifier,
      dropoffLocationIdentifier:
        returnDifferent && dropoffLocation
          ? dropoffLocation.identifier
          : pickupLocation.identifier,
      perkspotUserId: sessionInfo?.userDetails.tenantUserId,
      communityId: sessionInfo?.userDetails.communityId,
    });
    window.location.href = url;
  };

  const isFormValid =
    pickupDate != null &&
    dropoffDate != null &&
    pickupTime != null &&
    dropoffTime != null &&
    driverAge != null &&
    driverAge.trim() !== "" &&
    pickupLocation != null &&
    (!returnDifferent || dropoffLocation != null);

  return (
    <SearchBarContainer>
      <div className={cn("top-row")}>
        <CarLocationAutocomplete
          id="cars-pickup-location"
          label={t("core-cars.search.pickup")}
          onChange={(location) => setPickupLocation(location)}
          defaultValue={pickupLocation}
          className={cn("location-autocomplete")}
        />
        {returnDifferent ? (
          <CarLocationAutocomplete
            id="cars-dropoff-location"
            label={t("core-cars.search.dropoff")}
            onChange={(location) => setDropoffLocation(location)}
            defaultValue={dropoffLocation}
            className={cn("location-autocomplete")}
            returnToSameLocation={() => {
              setDropoffLocation(undefined);
              setReturnDifferent(false);
            }}
          />
        ) : (
          <div className={cn("return-checkbox")}>
            <Checkbox
              className={cn("checkbox")}
              id="return-different"
              checked={returnDifferent}
              onClick={() => setReturnDifferent(true)}
            />
            <label htmlFor="return-different">
              {t("core-cars.search.differentLocationLabel")}
            </label>
          </div>
        )}
      </div>
      <div className={cn("bottom-row")}>
        <DateRangePicker
          classNames={{ button: cn("date-picker") }}
          startDateLabel={t("core-cars.search.pickupDate")}
          endDateLabel={t("core-cars.search.dropoffDate")}
          startDate={pickupDate}
          endDate={dropoffDate}
          onStartDateChangeHandler={(date) => {
            setPickupDate(date);
          }}
          onEndDateChangeHandler={(date) => {
            setDropoffDate(date);
          }}
        />
        <div className={cn("time-pickers-row")}>
          <TimePicker
            label={t("core-cars.search.pickupTime")}
            value={pickupTime ? new Date(pickupTime) : undefined}
            onChange={(date) => {
              setPickupTime(date?.toISOString());
            }}
            className={cn("time-picker")}
            steps={30}
            disablePortal={true}
            hasInlineLabel={true}
            startIcon={null}
          />
          <TimePicker
            label={t("core-cars.search.dropoffTime")}
            value={dropoffTime ? new Date(dropoffTime) : undefined}
            onChange={(date) => {
              setDropoffTime(date?.toISOString());
            }}
            className={cn("time-picker")}
            steps={30}
            disablePortal={true}
            hasInlineLabel={true}
            startIcon={null}
          />
        </div>
        <InputText
          className={cn("age")}
          label={t("core-cars.search.driverAge")}
          type="number"
          startContent={<Icon iconName={IconNameEnum.guest} />}
          onChange={(e) => {
            setDriverAge(e.target.value);
          }}
          value={driverAge}
        />
        <ActionButton
          disabled={!isFormValid}
          message={t("core-cars.search.searchButton")}
          className={cn("search-button")}
          onClick={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        />
      </div>
    </SearchBarContainer>
  );
};
